import { datadogLogs, StatusType } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import packageInfo from '../../package.json';

let isDatadogInitialized = false;

/**
 * Query parameters that must not reach telemetry. `message` can carry text a patient typed.
 * Nothing in the portal writes it into its own URLs any more (every prefill of the support-request
 * form travels in memory, pages/Messages/supportRequestDraft), but an inbound NewSupportRequest
 * deep link still arrives as `?action=…&message=…`, so the landing URL carries it until the link
 * resolves. `aiThreadId` is the AI chat's thread id, and chat ids count as PHI (LMD-1821). The portal
 * keeps it out of its own URLs since Sept 28 (hooks/useSelectedAiThreadId), but a link made before
 * that still lands with it. Everything else the portal puts in a query string is an id, a step key
 * or a fixed code.
 *
 * `mask-user-input` masks DOM text, not URLs, and the SDK copies `location.href` into
 * `view.url` and the next view's `view.referrer` as-is. Redacting these values in `beforeSend`
 * keeps them out; PostHog runs the same redaction (hooks/useAppAnalytics).
 */
// `vt` is here as well as in SENSITIVE_FRAGMENT_PARAMS because the fragment is the shape we send,
// not the only shape that arrives: a link rewriter can percent-encode the `#`, and a templating
// mistake can emit `?`. A token that reaches the query string is already in the access log, which
// is all the more reason not to let it into RUM and PostHog as well.
const SENSITIVE_QUERY_PARAMS = ['message', 'aiThreadId', 'vt'];

/**
 * Fragment parameters that must not reach telemetry either.
 *
 * `vt` is the click-to-verify link's token (pages/VerifyIdentity/utils/verifyLinkToken): a
 * single-use credential, redeemable for a few minutes, whose payload names the patient. It rides
 * in the fragment precisely so that it never reaches a server — which buys nothing if the
 * browser-side SDKs ship it to Datadog and PostHog instead, and they do. The fragment used to be
 * passed through here untouched, on the reasoning that a fragment never leaves the client: true
 * of our servers, false of our vendors (LMD-2273 review).
 *
 * `src/index.tsx` takes the fragment out of the address bar before either SDK starts. This is the
 * second layer, for whatever the first one cannot reach.
 */
const SENSITIVE_FRAGMENT_PARAMS = ['vt'];
const REDACTED_QUERY_VALUE = 'REDACTED';

// A value can also sit URL-encoded inside another parameter's value: an analytics beacon carries
// the page address that way (`dl=https%3A%2F%2F…%3FaiThreadId%3D…`), and Datadog records the
// beacon as a resource. Thread ids are alphanumeric, so the encoded name and value find it.
const NESTED_THREAD_ID_PATTERN = /(aiThreadId(?:%3D|%253D))[A-Za-z0-9]+/gi;

// Same shape for the link token, which a beacon carries as `…%23vt%3D<jwt>`. Anchored on the
// separator immediately before the name, so it cannot fire on some other parameter whose name
// merely ends in `vt`; the value charset is base64url plus the JWT's two dots.
// `?` and its encodings are in the separator group for the same reason `vt` is a query param
// above — the mangled link shapes put the token after a `?` rather than a `#`.
const NESTED_LINK_TOKEN_PATTERN =
  /((?:%23|%2523|%26|%2526|%3F|%253F|[#&?])vt(?:%3D|%253D|=))[A-Za-z0-9._-]+/gi;

/**
 * Rewrites `source` — a query string or a fragment, without its leading `?`/`#` — with every named
 * parameter's value replaced by a fixed marker. Returns `null` when none of them are present, so
 * the caller can hand back the original text rather than a `URLSearchParams` re-encoding of it.
 */
const redactParams = (source: string, names: string[]): string | null => {
  const params = new URLSearchParams(source);
  const present = names.filter((name) => params.has(name));
  if (!present.length) return null;

  present.forEach((name) => params.set(name, REDACTED_QUERY_VALUE));

  return params.toString();
};

/**
 * Replaces the value of every sensitive query *and fragment* parameter in `url` with a fixed
 * marker, leaving the rest of the URL intact. Works on absolute and relative URLs alike.
 */
const redactSensitiveUrlParams = (rawUrl: string): string => {
  const url = rawUrl
    .replace(NESTED_THREAD_ID_PATTERN, `$1${REDACTED_QUERY_VALUE}`)
    .replace(NESTED_LINK_TOKEN_PATTERN, `$1${REDACTED_QUERY_VALUE}`);

  const hashStart = url.indexOf('#');
  const queryEnd = hashStart === -1 ? url.length : hashStart;
  // Searched in the pre-fragment part only: a `?` that appears solely inside the fragment belongs
  // to the fragment and is not the start of a query string.
  const queryStart = url.slice(0, queryEnd).indexOf('?');

  let result = url;

  if (hashStart !== -1) {
    const fragment = redactParams(url.slice(hashStart + 1), SENSITIVE_FRAGMENT_PARAMS);
    if (fragment !== null) result = `${url.slice(0, hashStart + 1)}${fragment}`;
  }

  if (queryStart !== -1) {
    const query = redactParams(result.slice(queryStart + 1, queryEnd), SENSITIVE_QUERY_PARAMS);
    if (query !== null) {
      result = `${result.slice(0, queryStart + 1)}${query}${result.slice(queryEnd)}`;
    }
  }

  return result;
};

type ViewUrlTelemetry = { referrer?: string; url: string };

/** Both SDKs allow `view.url` / `view.referrer` to be rewritten in `beforeSend`; do it in place. */
const redactViewUrls = (view: ViewUrlTelemetry | undefined) => {
  if (!view) return;

  view.url = redactSensitiveUrlParams(view.url);
  if (view.referrer) view.referrer = redactSensitiveUrlParams(view.referrer);
};

const initDatadog = () => {
  if (isDatadogInitialized || import.meta.env.VITE_ENV === 'local') return;

  const applicationId = import.meta.env.VITE_DATA_DOG_APP_ID;
  const clientToken = import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) return;

  const baseUrl = import.meta.env.VITE_BASE_URL;
  const allowedTracingUrls = baseUrl ? [baseUrl] : [];

  datadogRum.init({
    allowedTracingUrls,
    applicationId,
    beforeSend: (event) => {
      redactViewUrls(event.view);
      // The page's own requests, analytics beacons included, carry the page address in their URLs.
      if (event.type === 'resource')
        event.resource.url = redactSensitiveUrlParams(event.resource.url);

      if (event.type !== 'error') return true;

      const source = `${event.error?.message ?? ''} ${event.error?.stack ?? ''}`;

      if (source.includes('node_modules')) return false;
      // Filter out noisy browser event cancel errors that are safe to ignore.
      // We primarily key on "IgnoredEventCancel" to avoid being overly strict
      // about the exact message/stack formatting across browsers.
      if (source.includes('IgnoredEventCancel')) return false;
      // Zoom SDK: benign when user leaves preview before video starts
      if (source.includes('VideoNotStartedError')) return false;
      return true;
    },
    clientToken,
    defaultPrivacyLevel: 'mask-user-input',
    env: import.meta.env.VITE_ENV,
    excludedActivityUrls: [/analytics/, /perfalytics/, /facebook/, /google/, /tiktok/, /pixel/],
    service: 'patient_fe',
    sessionReplaySampleRate: 100,
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    traceSampleRate: 20,
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: packageInfo.version
  });

  datadogLogs.init({
    beforeSend: (log) => {
      redactViewUrls(log.view);
      return true;
    },
    clientToken,
    env: import.meta.env.VITE_ENV,
    // forwardConsoleLogs: ['error'],
    forwardErrorsToLogs: true,
    service: 'patient_fe',
    site: 'datadoghq.com',
    version: packageInfo.version
  });

  isDatadogInitialized = true;
};

const logDataDogEvent = ({
  context,
  error,
  level = 'info',
  message
}: {
  context?: object;
  error?: Error;
  level?: StatusType;
  message: string;
}) => {
  datadogLogs.logger[level](message, { ...context, isHandle: true }, error);
};

export { initDatadog, logDataDogEvent, redactSensitiveUrlParams };
